import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "../data/content";
import { Logo } from "./Logo";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transform-gpu border-b transition-[background-color,border-color,padding,box-shadow] duration-300 ${
        scrolled
          ? "bg-[#0D1B2A]/95 backdrop-blur-xl border-white/10 py-4 lg:py-2.5 shadow-[0_1px_20px_rgba(0,0,0,0.25)]"
          : "bg-[#0D1B2A] backdrop-blur-md border-transparent py-5 lg:py-3"
      }`}
    >
      <nav className="relative z-50 mx-auto max-w-7xl px-6 lg:px-8 flex items-center justify-between gap-4 lg:gap-8">
        <div className="flex shrink-0 items-center gap-4">
          <Logo dark imgClassName="h-16 lg:h-20 w-auto" />
          <div className="hidden 2xl:flex items-center gap-3.5">
            <span className="h-7 w-px bg-white/15" aria-hidden="true" />
            <span className="text-base italic font-medium tracking-tight text-white/55 whitespace-nowrap">
              Are you ready to pivot?
            </span>
          </div>
        </div>

        {/* Tagline centered in the free space on mobile/tablet (links are hidden below lg) */}
        <div className="flex-1 min-w-0 px-2 flex justify-center lg:hidden">
          <span className="text-center text-[13px] leading-tight italic font-medium text-white/55 whitespace-nowrap">
            Are you ready to pivot?
          </span>
        </div>

        <div className="hidden lg:flex items-center gap-6 xl:gap-8">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === "/"}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative whitespace-nowrap text-sm font-semibold tracking-wide transition-colors duration-200 ${
                  isActive ? "text-white" : "text-white/70 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {link.label}
                  <span
                    className={`absolute -bottom-1.5 left-0 h-0.5 bg-[#FD8924] transition-all duration-300 ${
                      isActive ? "w-full" : "w-0"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="nav-cta-button"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#FD8924] px-4 py-1.5 text-xs font-semibold text-white hover:bg-[#D7741F] active:scale-95 transition-[background-color,transform] duration-200 whitespace-nowrap"
          >
            Start a Conversation
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden text-white p-2"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>
    </header>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden fixed inset-0 z-40 bg-[#0D1B2A] overflow-y-auto">
          <div className="px-6 pt-24 pb-8 flex flex-col gap-5 landscape:max-lg:gap-3">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-lg font-semibold ${isActive ? "text-[#FD8924]" : "text-white"}`
                }
              >
              {link.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="mobile-nav-cta"
              className="mt-2 landscape:max-lg:mt-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#FD8924] px-5 py-3 landscape:max-lg:py-2 text-sm font-semibold text-white"
            >
              Start a Conversation
            </Link>
          </div>
        </div>
      )}
    </>
  );
};
